import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Button from '../../components/Button';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import DatePicker from '../../components/DatePicker';
import BulkUploadIcon from '../../assets/icons/bulk-upload-icon.svg?react';
import moment from 'moment';

interface EmployeeOnboardingStepOneProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
  roles: {
    label: string;
    value: string;
  }[];
  departments: {
    label: string;
    value: string;
  }[];
}

const EmployeeOnboardingStepOne: React.FC<EmployeeOnboardingStepOneProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  roles,
  departments,
}) => {
  const employeeOnboardingStepOneClasses = clsx(otherClasses);
  const dispatch = useDispatch();

  return (
    <div
      className={employeeOnboardingStepOneClasses}
      data-testid="employee-onboarding-step-one"
    >
      <div className="flex items-center justify-between">
        <div className="flex max-w-818 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <h1 className="h3">Employee Onboarding</h1>

            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Add new employees to EHS Navigator
            </p>
          </div>
        </div>
        <Button
          size="small"
          otherClasses="hidden lg:flex"
          href="/bulk-user-creation"
          variant="primary"
          // onClick={() => dispatch(toggleModal('add-department'))}
        >
          <BulkUploadIcon className="h-4 w-4 md:h-5 md:w-5 [&>path]:fill-current" />
          Bulk Upload
        </Button>
      </div>

      <div className="mt-15 mb-8 flex w-full max-w-955 flex-col gap-6 lg:mt-12">
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button -mb-1 text-lg leading-5 font-medium lg:hidden">
          Add new employees to EHS Navigator
        </p>
        <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-xl leading-6 font-bold">
          Internal Details
        </h6>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            onBlur={formHandler?.handleBlur}
            label="Employee ID*"
            errorMessage={
              formHandler?.touched.employeeId && formHandler?.errors.employeeId
                ? formHandler?.errors.employeeId
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.employeeId}
            type="text"
            placeholder="EMP-102938"
            onChange={formHandler?.handleChange}
            name="employeeId"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
      </div>
      <div className="mb-8 flex w-full max-w-955 flex-col gap-6">
        <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-xl leading-6 font-bold">
          Personal Details
        </h6>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            onBlur={formHandler?.handleBlur}
            label="Full Name*"
            errorMessage={
              formHandler?.touched.fullName && formHandler?.errors.fullName
                ? formHandler?.errors.fullName
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.fullName}
            type="text"
            placeholder="John smith"
            onChange={formHandler?.handleChange}
            name="fullName"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
          <Input
            onBlur={formHandler?.handleBlur}
            label="Email Address*"
            errorMessage={
              formHandler?.touched.email && formHandler?.errors.email
                ? formHandler?.errors.email
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.email}
            type="email"
            placeholder="johnsmith@gmail.com"
            onChange={formHandler?.handleChange}
            name="email"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <DatePicker
            otherClasses={{ container: 'w-full' }}
            label="Date of Birth*"
            name="date"
            startMonth={moment([1950, 0]).toDate()}
            // endMonth={moment().subtract(18, 'years').toDate()}
            endMonth={moment().toDate()}
            value={formHandler?.values?.dob}
            placeholder="Select date of birth"
            // onBlur={formHandler?.handleBlur}
            onBlur={() => {
              if (formHandler?.values?.dob) {
                formHandler?.setFieldTouched('dob', true);
              }
            }}
            disabled={formHandler.isSubmitting}
            onChange={(e) => {
              formHandler?.setFieldValue('dob', e, true);
              // formHandler?.setFieldValue('dateOfHire', '', true);
            }}
            errorMessage={
              formHandler?.touched?.dob && formHandler?.errors.dob
                ? formHandler?.errors.dob
                : ''
            }
          />
          <DatePicker
            otherClasses={{ container: 'w-full' }}
            label="Date of Hire*"
            name="dateOfHire"
            // startMonth={
            //   formHandler?.values?.dob
            //     ? moment(formHandler.values.dob).add(18, 'years').toDate()
            //     : new Date(1950, 0)
            // }
            // endMonth={new Date()}
            startMonth={moment([1950, 0]).toDate()}
            endMonth={moment().toDate()}
            value={formHandler?.values?.dateOfHire}
            placeholder="Select date of hire"
            onBlur={() => {
              if (formHandler?.values?.dateOfHire) {
                formHandler?.setFieldTouched('dateOfHire', true);
              }
            }}
            disabled={formHandler.isSubmitting}
            onChange={(e) => {
              formHandler?.setFieldValue('dateOfHire', e, true);
            }}
            errorMessage={
              formHandler?.touched?.dateOfHire && formHandler?.errors.dateOfHire
                ? formHandler?.errors.dateOfHire
                : ''
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            onBlur={formHandler.handleBlur}
            label="Contact Number*"
            errorMessage={
              formHandler?.touched.contact && formHandler?.errors.contact
                ? formHandler?.errors.contact
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.contact}
            type="tel"
            placeholder="Enter contact number"
            onChange={formHandler.handleChange}
            name="contact"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
      </div>
      <div className="mb-8 flex w-full max-w-955 flex-col gap-6">
        <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-xl leading-6 font-bold">
          Designation
        </h6>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Select
            label="Department*"
            list={departments}
            value={formHandler.values.department}
            disabled={formHandler.isSubmitting}
            placeholder="Departments"
            otherClasses={{ container: 'w-full' }}
            onSelect={(e) => formHandler?.setFieldValue('department', e, true)}
            errorMessage={
              formHandler?.touched.department && formHandler?.errors.department
                ? formHandler?.errors.department
                : ''
            }
          />
          <Input
            onBlur={formHandler?.handleBlur}
            label="Job Title*"
            errorMessage={
              formHandler?.touched.jobTitle && formHandler?.errors.jobTitle
                ? formHandler?.errors.jobTitle
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.jobTitle}
            type="text"
            placeholder="Electrician"
            onChange={formHandler?.handleChange}
            name="jobTitle"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
      </div>
      <div className="mb-8 flex w-full max-w-955 flex-col gap-6">
        <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-xl leading-6 font-bold">
          Employee Role
        </h6>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Select
            label="Role*"
            list={roles}
            value={formHandler.values.role}
            disabled={formHandler.isSubmitting}
            placeholder="Role"
            otherClasses={{
              container: 'w-full',
              listContainer: 'top-auto bottom-18',
            }}
            onSelect={(e) => formHandler?.setFieldValue('role', e, true)}
            errorMessage={
              formHandler?.touched.role && formHandler?.errors.role
                ? formHandler?.errors.role
                : ''
            }
          />
        </div>
      </div>
      <Button
        type="button"
        size={useButtonSizeSmaller()}
        onClick={() => handleSteps(2)}
      >
        <SidebarCrossIcon className="h-4 w-4 rotate-45 [&>g>path]:fill-current" />
        Add New User
      </Button>
    </div>
  );
};

export default EmployeeOnboardingStepOne;
